<template>
  <div
    v-frag
    class="prism-elite-app"
    :class="{ 'cinema-mode-active': cinemaMode }"
  >
    <CardIeaAppMain :colorCard="colorCurso" title="">
      <v-col cols="12" class="pa-0 main-container-glass">
        <!-- Cinema Overlay -->
        <div v-if="cinemaMode" class="cinema-overlay" @click="cinemaMode = false"></div>

        <!-- Top Bar / Context -->
        <div
          class="prism-top-bar d-flex align-center justify-space-between px-8 py-5"
        >
          <div class="d-flex align-center">
            <v-btn
              icon
              @click="$router.push({ name: 'MisMaterias' })"
              class="mr-4 glass-btn"
              large
            >
              <v-icon color="primary">mdi-arrow-left-circle-outline</v-icon>
            </v-btn>
            <div>
              <h1
                class="text-h6 font-weight-black prism-title mb-0"
              >
                {{ tituloCurso }}
              </h1>
              <div class="text-caption grey--text font-weight-medium">
                Sesión {{ selectedClaseIndex + 1 }} • {{ clasesMateria.length }} Módulos
              </div>
            </div>
          </div>
          <div class="d-none d-md-flex align-center" style="width: 350px">
            <div class="mr-6 text-right">
              <div class="text-overline grey--text lh-1 mb-1">PROGRESO</div>
              <div class="text-h6 font-weight-black primary--text lh-1">
                {{ progreso }}%
              </div>
            </div>
            <div class="flex-grow-1">
              <v-progress-linear
                :value="progreso"
                color="primary"
                rounded
                height="8"
                class="prism-progress"
              ></v-progress-linear>
            </div>
          </div>
        </div>

        <v-divider class="prism-divider"></v-divider>

        <!-- Main 3-Column Content -->
        <v-row no-gutters class="prism-grid">
          <!-- Left: Navigator (20%) -->
          <v-col
            cols="12"
            md="2"
            class="prism-sidebar-left glass-sidebar"
          >
            <div class="pa-6">
              <div class="text-overline font-weight-black mb-6 primary--text letter-spaced">
                CLASES
              </div>
              <div v-if="clasesLoading" class="text-center py-10">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="24"
                  width="2"
                ></v-progress-circular>
                <div class="text-tiny grey--text mt-2 font-weight-black">SINCRONIZANDO...</div>
              </div>
              <div v-else-if="clasesError" class="text-center py-10 px-4">
                <v-icon color="error" small>mdi-alert-circle-outline</v-icon>
                <div class="text-tiny error--text mt-1 font-weight-bold">{{ clasesError }}</div>
                <v-btn x-small text color="primary" class="mt-2" @click="cargarClasesMateria">Reintentar</v-btn>
              </div>
              <div v-else-if="!clasesMateria.length" class="text-center py-10 px-4">
                <v-icon color="grey" small>mdi-folder-open-outline</v-icon>
                <div class="text-tiny grey--text mt-1">No hay módulos disponibles.</div>
              </div>
              <div v-else class="prism-nav-list">
                <div
                  v-for="(clase, i) in clasesMateria"
                  :key="clase.id || i"
                  class="prism-nav-item mb-3"
                  :class="{
                    'is-active': selectedClaseIndex === i,
                    'is-completed': claseFinalizada(clase)
                  }"
                  @click="seleccionarClase(i)"
                >
                  <div class="active-glow"></div>
                  <div class="nav-content py-4 px-5">
                    <div
                      class="text-body-2 font-weight-bold prism-nav-title text-truncate"
                    >
                      {{ clase.nombre }}
                    </div>
                    <div class="d-flex align-center mt-1">
                      <div
                        class="status-dot mr-2"
                        :class="claseFinalizada(clase) ? 'bg-success' : 'bg-grey'"
                      ></div>
                      <span class="text-tiny grey--text text--darken-1 font-weight-bold">
                        <span>{{ claseFinalizada(clase) ? "Completado" : "Módulo " + (i+1) }}</span>
                        <span v-if="clase.total_actividades > 0" class="ml-2">
                           — {{ clase.actividades_completadas }}/{{ clase.total_actividades }} Desafíos
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>

          <!-- Center: Theater (55%) -->
          <v-col
            cols="12"
            md="7"
            class="prism-main-focus"
            :class="{ 'cinema-focus': cinemaMode }"
          >
            <div class="pa-8 theater-wrap">
              <!-- Video Stage -->
              <div
                class="prism-video-card mb-10"
                :class="{ 'elevation-24': cinemaMode }"
              >
                <div class="video-header-actions d-flex justify-end pa-2">
                  <v-btn
                    icon
                    small
                    @click="cinemaMode = !cinemaMode"
                    class="glass-btn-sm"
                    :color="cinemaMode ? 'warning' : 'white'"
                  >
                    <v-icon small>
                      {{ cinemaMode ? 'mdi-television-off' : 'mdi-television-play' }}
                    </v-icon>
                  </v-btn>
                </div>
                <div
                  class="video-stage black overflow-hidden d-flex align-center justify-center"
                >
                  <v-responsive :aspect-ratio="16 / 9" style="width: 100%">
                    <div
                      class="d-flex align-center justify-center fill-height relative"
                    >
                      <youtube
                        v-if="currentVideoId"
                        :video-id="currentVideoId"
                        width="100%"
                        height="100%"
                        ref="youtube"
                        :player-vars="{ 
                          autoplay: autoPlayCurrentVideo ? 1 : 0, 
                          modestbranding: 1, 
                          rel: 0, 
                          origin: localOrigin, 
                          widget_referrer: localOrigin 
                        }"
                        @ready="onVideoReady"
                        @playing="onVideoPlaying"
                        @paused="onVideoPaused"
                        @ended="onVideoEnded"
                      />
                      <!-- Videos nuevos: URL directa (DigitalOcean Spaces) en vez
                           de un id de YouTube. Mismos handlers, mismo tracking. -->
                      <video
                        v-else-if="currentVideoUrl"
                        :src="currentVideoUrl"
                        ref="videoHtml"
                        width="100%"
                        height="100%"
                        preload="metadata"
                        controls
                        playsinline
                        :autoplay="autoPlayCurrentVideo"
                        @loadedmetadata="onVideoReady"
                        @playing="onVideoPlaying"
                        @pause="onVideoPaused"
                        @ended="onVideoEnded"
                      />
                      <div v-else class="text-center py-16">
                        <div class="empty-video-icon mb-4">
                          <v-icon size="82" color="rgba(255,255,255,0.2)">
                            mdi-play-circle-outline
                          </v-icon>
                        </div>
                        <div
                          class="grey--text text--lighten-1 font-weight-bold"
                        >
                          Preparando material audiovisual...
                        </div>
                      </div>
                    </div>
                  </v-responsive>
                </div>
                <!-- Premium Controls -->
                <div
                  class="prism-controls pa-5 d-flex align-center justify-space-between"
                >
                  <v-btn
                    depressed
                    class="glass-action-btn"
                    @click="irAnteriorContenido"
                    :disabled="!anteriorDisponible"
                  >
                    <v-icon left small>mdi-chevron-left</v-icon> Anterior
                  </v-btn>
                  <div class="d-flex align-center">
                     <v-chip
                        v-for="(v, vi) in videosClaseSeleccionada"
                        :key="v.id || vi"
                        class="mx-1 video-pill"
                        :class="{ 'active-pill': selectedVideoIndex === vi }"
                        x-small
                        @click="seleccionarVideo(vi)"
                     ></v-chip>
                  </div>
                  <v-btn
                    depressed
                    color="primary"
                    class="glass-action-btn primary-glass"
                    @click="irSiguienteContenido"
                    :disabled="!siguienteDisponible"
                  >
                    Siguiente <v-icon right small>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Tabs Section Premium -->
              <v-tabs
                v-model="activeTab"
                class="prism-tabs mb-8"
                hide-slider
              >
                <v-tab class="text-none prism-tab-item">
                  <v-icon left small>mdi-text-box-outline</v-icon> Material
                </v-tab>
                <v-tab class="text-none prism-tab-item">
                  <v-icon left small>mdi-sword-cross</v-icon> Desafíos
                </v-tab>
                <v-tab class="text-none prism-tab-item">
                  <v-icon left small>mdi-account-group-outline</v-icon> Comunidad
                </v-tab>
                <v-tab class="text-none prism-tab-item">
                  <v-icon left small>mdi-folder-open-outline</v-icon> Recursos
                </v-tab>
              </v-tabs>

              <div class="prism-tab-content">
                <!-- Tab: Content -->
                <div v-if="activeTab === 0" class="fade-in">
                  <div class="d-flex align-center justify-space-between mb-8">
                    <h2 class="text-h5 font-weight-black prism-heading">
                      Apuntes Técnicos
                    </h2>
                    <v-btn
                      text
                      color="primary"
                      class="text-none font-weight-bold"
                      @click="abrirDetalleClase"
                    >
                      <v-icon left small>mdi-arrow-expand</v-icon> Enfoque Total
                    </v-btn>
                  </div>
                  <div v-if="claseDetalleLoading" class="pa-10 text-center">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      width="2"
                    ></v-progress-circular>
                  </div>
                  <v-card
                     v-else
                     flat
                     class="pa-8 rounded-xl glass-card-heavy content-render"
                     v-html="claseDetalleHtml || descripcion"
                  ></v-card>
                </div>

                <!-- Tab: Activities -->
                <div v-if="activeTab === 1" class="fade-in">
                  <div class="d-flex align-center justify-space-between mb-8">
                    <h2 class="text-h5 font-weight-black prism-heading">
                      Laboratorio Práctico
                    </h2>
                  </div>
                  <div
                    v-if="!actividades.length && !actividadesCargando"
                    class="text-center py-16 glass-card-light rounded-xl"
                  >
                    <v-icon size="64" color="grey lighten-3">
                      mdi-flask-empty-outline
                    </v-icon>
                    <div class="grey--text mt-4 font-weight-medium">
                      Este módulo no requiere ejecución práctica.
                    </div>
                  </div>
                  <div v-else-if="actividadesCargando" class="text-center py-16">
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    <div class="mt-4 grey--text">Cargando actividades...</div>
                  </div>
                  <div v-else>
                    <v-card
                      v-for="act in actividades"
                      :key="act.id"
                      flat
                      class="pa-8 mb-6 rounded-xl glass-card-interactive"
                    >
                      <div class="d-flex align-center justify-space-between mb-4">
                         <div class="text-overline primary--text font-weight-black">
                           {{ (act.tipo_entrega || 'Actividad').toUpperCase() }}
                         </div>
                         <v-chip
                           x-small
                           :color="act.entrega ? 'success' : 'grey lighten-4'"
                           :class="act.entrega ? 'white--text' : 'grey--text'"
                           class="font-weight-bold"
                         >
                           {{ act.entrega ? (act.entrega.corregido ? '✓ CORREGIDO' : '✓ ENTREGADO') : 'PENDIENTE' }}
                         </v-chip>
                      </div>
                      
                      <div class="text-h6 font-weight-black mb-3" v-html="act.title"></div>
                      <div class="text-body-2 grey--text text--darken-3 mb-6" v-html="act.content"></div>

                       <div class="activity-render">
                          <v-textarea
                            v-if="act.tipo_entrega !== 'subida_pdf'"
                            v-model="act.respuestaAlumno"
                            outlined
                            flat
                            dense
                            no-resize
                            rows="4"
                            label="Tu respuesta"
                            placeholder="Escribe aquí tu análisis o respuesta técnica..."
                            class="rounded-xl elite-textarea"
                            :disabled="Boolean(act.entrega && act.entrega.corregido)"
                          ></v-textarea>
                          
                          <v-file-input
                            v-else
                            v-model="act.archivoAlumno"
                            outlined
                            flat
                            dense
                            label="Adjuntar Guía Técnica (PDF)"
                            prepend-icon="mdi-file-pdf-box"
                            accept=".pdf"
                            class="rounded-xl elite-textarea"
                            :disabled="Boolean(act.entrega && act.entrega.corregido)"
                          ></v-file-input>
                       </div>
                         <div class="d-flex justify-end mt-4">
                            <v-btn
                              color="primary"
                              depressed
                              class="rounded-lg px-8 font-weight-black"
                              :loading="act.enviando"
                              :disabled="(act.tipo_entrega === 'subida_pdf' ? !act.archivoAlumno : !act.respuestaAlumno) || Boolean(act.entrega && act.entrega.corregido)"
                              @click="enviarRespuestaActividad(act)"
                            >
                              {{ act.entrega ? 'ACTUALIZAR ENTREGA' : 'ENVIAR RESPUESTA' }}
                            </v-btn>
                         </div>

                         <div v-if="act.entrega && act.entrega.correccion_comentario" class="mt-6 pa-4 rounded-lg bg-light-blue">
                            <div class="text-caption font-weight-bold primary--text mb-1">
                               COMENTARIO DOCENTE
                            </div>
                            <div class="text-body-2 grey--text text--darken-3">
                               {{ act.entrega.correccion_comentario }}
                            </div>
                         </div>
                    </v-card>
                  </div>
                </div>

                <!-- Tab: Forum -->
                <div v-if="activeTab === 2" class="fade-in">
                  <div class="d-flex align-center justify-space-between mb-8">
                    <h2 class="text-h5 font-weight-black prism-heading">
                      Salón de Discusión
                    </h2>
                    <v-btn
                      color="primary"
                      depressed
                      rounded
                      class="text-none font-weight-black"
                      @click="abrirDialogForoNuevo"
                    >
                      <v-icon left small>mdi-plus</v-icon> Iniciar Debate
                    </v-btn>
                  </div>
                  <div class="prism-forum-list">
                    <v-card
                      v-for="foritem in forosMateria"
                      :key="foritem.id"
                      class="mb-4 rounded-xl glass-card-light forum-entry"
                      flat
                      @click="abrirForoDetalle()"
                    >
                      <div class="pa-7">
                        <div class="d-flex justify-space-between align-center">
                          <div class="d-flex align-center">
                            <div class="forum-icon-box mr-4">
                               <v-icon color="primary" small>mdi-comment-text-multiple-outline</v-icon>
                            </div>
                            <div>
                               <div class="text-h6 font-weight-bold mb-1">
                                 {{ foritem.title }}
                               </div>
                               <div class="text-caption grey--text font-weight-black">
                                 {{ (foritem.comments || []).length }} APORTES PROFESIONALES
                               </div>
                            </div>
                          </div>
                          <v-btn icon color="grey lighten-1">
                            <v-icon>mdi-chevron-right</v-icon>
                          </v-btn>
                        </div>
                      </div>
                    </v-card>
                  </div>
                </div>

                <!-- Tab: Resources -->
                <div v-if="activeTab === 3" class="fade-in">
                  <div class="d-flex align-center justify-space-between mb-8">
                    <h2 class="text-h5 font-weight-black prism-heading">
                      Centro de Recursos
                    </h2>
                  </div>
                  <v-row>
                    <v-col cols="12" md="6" v-if="cursoGoogleDriveUrl">
                      <v-card
                        flat
                        class="pa-8 rounded-xl glass-card-interactive fill-height"
                        @click="abrirRecursoCurso"
                      >
                        <div class="d-flex align-center mb-6">
                           <div class="resource-icon-box drive mr-4">
                              <v-icon color="success">mdi-google-drive</v-icon>
                           </div>
                           <div class="text-overline success--text font-weight-black lh-1">
                             REPOSITORIO GENERAL
                           </div>
                        </div>
                        <div class="text-h6 font-weight-black mb-2">
                          Biblioteca en la Nube
                        </div>
                        <p class="text-body-2 grey--text mb-6">
                          Accede a toda la documentación extendida, bibliografías y archivos de soporte del curso.
                        </p>
                        <v-btn text color="success" class="px-0 text-none font-weight-black">
                          Explorar carpeta <v-icon right small>mdi-arrow-right</v-icon>
                        </v-btn>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="6" v-if="claseSeleccionada && claseSeleccionada.link_pdf">
                      <v-card
                        flat
                        class="pa-8 rounded-xl glass-card-interactive fill-height"
                        @click="abrirRecursoPDF"
                      >
                        <div class="d-flex align-center mb-6">
                           <div class="resource-icon-box pdf mr-4">
                              <v-icon color="error">mdi-file-pdf-box</v-icon>
                           </div>
                           <div class="text-overline error--text font-weight-black lh-1">
                             MATERIAL DE SESIÓN
                           </div>
                        </div>
                        <div class="text-h6 font-weight-black mb-2">
                          Guía Técnica PDF
                        </div>
                        <p class="text-body-2 grey--text mb-6">
                          Descarga el material específico de este módulo para estudio offline y referencias rápidas.
                        </p>
                        <v-btn text color="error" class="px-0 text-none font-weight-black">
                          Descargar archivo <v-icon right small>mdi-download</v-icon>
                        </v-btn>
                      </v-card>
                    </v-col>
                    <v-col cols="12" v-if="!cursoGoogleDriveUrl && (!claseSeleccionada || !claseSeleccionada.link_pdf)">
                       <div class="text-center py-16 glass-card-light rounded-xl">
                          <v-icon size="64" color="grey lighten-3">mdi-folder-search-outline</v-icon>
                          <div class="grey--text mt-4 font-weight-medium">
                            No se han adjuntado recursos técnicos adicionales para esta sesión.
                          </div>
                       </div>
                    </v-col>
                  </v-row>
                  
                </div>
              </div>
            </div>
          </v-col>

          <!-- Right: Assistance & Context (25%) (PERSISTENT) -->
          <v-col
            cols="12"
            md="3"
            class="prism-sidebar-right glass-sidebar border-left"
          >
            <div class="pa-6 sticky-sidebar">
              <!-- IA Elite Console -->
              <v-card
                flat
                class="prism-ai-console mb-8 pa-7 rounded-xl overflow-hidden"
              >
                <div class="d-flex align-center mb-6">
                  <div class="elite-ia-avatar mr-4">
                    <div class="ia-ring"></div>
                    <v-icon color="white" small>mdi-auto-fix</v-icon>
                  </div>
                  <div>
                    <div class="text-subtitle-2 font-weight-black primary--text lh-1 mb-1">
                      IA ACADÉMICA
                    </div>
                    <div class="text-tiny grey--text font-weight-black uppercase">
                      ONLINE • ANALIZANDO CONTEXTO
                    </div>
                  </div>
                </div>

                <div class="prism-ai-viewport mb-6" ref="chatBox">
                  <div
                    v-if="!iaReply && !iaLoading"
                    class="text-caption grey--text text-center py-10 px-6 font-weight-medium italic"
                  >
                    "Consulta cualquier concepto técnico o solicita un resumen ejecutivo de la sesión."
                  </div>
                  <div
                    v-if="iaReply"
                    class="ai-elite-bubble mb-3 pa-5 rounded-xl animate-scale-up"
                  >
                    <div class="text-body-2 lh-relaxed text-assistant" v-html="iaReply"></div>
                  </div>
                  <div v-if="iaLoading" class="text-center py-6">
                    <div class="ia-scanning-bar mb-2"></div>
                    <div class="text-tiny primary--text font-weight-black">PROCESANDO CONSULTA...</div>
                  </div>
                </div>

                <div class="prism-ai-controls">
                  <v-textarea
                    v-model="iaQuestion"
                    placeholder="Escribir requerimiento..."
                    rows="2"
                    hide-details
                    outlined
                    flat
                    class="rounded-xl mb-3 elite-textarea"
                  ></v-textarea>
                  <v-btn
                    block
                    color="primary"
                    depressed
                    height="48"
                    class="rounded-xl font-weight-black letter-spaced"
                    @click="preguntarIA"
                    :loading="iaLoading"
                  >
                    EJECUTAR CONSULTA
                  </v-btn>
                </div>
              </v-card>

              <!-- Meet Elite -->
              <v-card
                v-if="urlMeet"
                flat
                class="pa-6 mb-8 rounded-xl elite-meet-card"
                @click="abrirMeet"
              >
                <div class="d-flex align-center mb-4">
                   <div class="live-pulse mr-3"></div>
                   <span class="text-overline font-weight-black error--text">SESIÓN EN VIVO</span>
                </div>
                <div class="text-body-2 font-weight-medium mb-5">
                  Conexión directa con el claustro docente.
                </div>
                <v-btn
                  block
                  depressed
                  color="error"
                  class="rounded-xl font-weight-black text-none"
                >
                  Unirse a la Videoconferencia
                </v-btn>
              </v-card>

            </div>
          </v-col>
        </v-row>
      </v-col>
    </CardIeaAppMain>
  </div>
</template>

<script>
// Logic core
import CardIeaAppMain from "../../components/CardIeaAppMain";

export default {
  name: "MateriaDetallePrismElite",
  components: {
    CardIeaAppMain
  },
  data() {
    return {
      activeTab: 0,
      cinemaMode: false,
      clasesMateria: [],
      selectedClaseIndex: 0,
      selectedVideoIndex: 0,
    actividadesClaseBackendPorId: {},
    actividadesCargando: false,
      currentVideoId: null,
      // URL del video cuando NO es de YouTube (link directo de DigitalOcean).
      // Excluyente con currentVideoId: uno u otro, nunca los dos.
      currentVideoUrl: null,
      clasesLoading: false,
      clasesError: "",
      claseDetalleHtml: "",
      claseDetalleLoading: false,
      iaQuestion: "",
      iaReply: "",
      iaLoading: false,
      urlMeet: "",
      cursoGoogleDriveUrl: "",
      vencimiento: "23/07/2026",
      progreso: 0,
      actividades: [],
      forosMateria: [],
      iaModelLabel: "Gemini Prism v4",
      descripcion: "Explora los contenidos avanzados del módulo.",
      autoPlayCurrentVideo: false,
      pendingStartSeconds: 0,
      progressInterval: null
    };
  },
  computed: {
    user() {
      return this.$store.state.user || {};
    },
    esDocente() {
      // En iea-bce se identifica por tipoUsuario o el campo docente en el objeto user
      return this.user.tipoUsuario === "profesor" || this.user.docente === true;
    },
    tituloCurso() {
      return this.$route.query.nombre || "Especialización de Vanguardia";
    },
    colorCurso() {
      return "#0F172A"; // Ultra Navy Deep
    },
    claseSeleccionada() {
      return this.clasesMateria[this.selectedClaseIndex] || null;
    },
    videosClaseSeleccionada() {
      if (!this.claseSeleccionada) return [];
      return Array.isArray(this.claseSeleccionada.materia_codigo_link)
        ? this.claseSeleccionada.materia_codigo_link
        : [];
    },
    anteriorDisponible() {
      return this.selectedClaseIndex > 0 || this.selectedVideoIndex > 0;
    },
    siguienteDisponible() {
       const videos = this.videosClaseSeleccionada;
       if (videos.length && this.selectedVideoIndex < videos.length - 1) return true;
       return this.selectedClaseIndex < this.clasesMateria.length - 1;
    },
    localOrigin() {
      return typeof window !== "undefined" ? window.location.origin : "";
    }
  },
  methods: {
    async cargarClasesMateria() {
      const codCatedra = this.$route.params.materiaId;
      this.clasesLoading = true;
      this.clasesError = "";
      try {
        const res = await this.$API.get({
          url: `materia-codigo/show/${codCatedra}`
        });
        
        if (res && res.materia_codigo_clase) {
          // Normalización para paridad con iea-public
          this.clasesMateria = res.materia_codigo_clase.map(clase => {
            const links = Array.isArray(clase.materia_codigo_link) ? clase.materia_codigo_link : [];
            return {
              ...clase,
              total_actividades: Number(clase.total_actividades || 0),
              actividades_completadas: Number(clase.actividades_completadas || 0),
              materia_codigo_link: links.map(link => {
                const log = (link.materia_codigo_log && link.materia_codigo_log[0]) || {};
                return {
                  ...link,
                  videoActualSegundo: Number(log.videoTranscurrido || 0),
                  videoDuracion: Number(log.videoDuracion || 0),
                  porcentaje: Number(log.videoPorcentaje || link.porcentaje || 0)
                };
              })
            };
          });

          this.urlMeet = res.link_meet;
          this.cursoGoogleDriveUrl = res.url_google_drive;
          this.actualizarProgreso();
          
          let initialClase = 0;
          let initialVideo = 0;
          if (res.clase !== undefined) initialClase = parseInt(res.clase);
          if (res.video !== undefined) initialVideo = parseInt(res.video);
          
          this.seleccionarClase(initialClase, initialVideo, false);
        } else {
          this.clasesError = "No se encontraron datos del curso.";
        }
      } catch (e) {
        this.clasesError = "Error de enlace con el servidor académico.";
      } finally {
        this.clasesLoading = false;
      }
    },
    actualizarProgreso() {
      if (this.clasesMateria.length > 0) {
        let completadas = 0;
        this.clasesMateria.forEach(c => {
           if (this.claseFinalizada(c)) completadas++;
        });
        this.progreso = Math.round((completadas / this.clasesMateria.length) * 100);
      }
    },
    async seleccionarClase(idx, videoIdx = 0, autoPlay = true) {
      if (idx < 0 || idx >= this.clasesMateria.length) return;
      
      // Guardar progreso en segundo plano para no bloquear la UI
      if (this.claseSeleccionada) {
         this.enviarEstadoVideo("paused");
      }

      this.selectedClaseIndex = idx;
      this.selectedVideoIndex = videoIdx;
      this.cargarActividadesClase(this.clasesMateria[idx].id);
      this.$nextTick(() => {
        this.autoPlayCurrentVideo = autoPlay;
        this.cargarVideoSeleccionado();
        this.cargarDetalleClase();
        this.cargarForosMateria();
      });
    },
    async seleccionarVideo(idx, autoPlay = true) {
      if (!this.videosClaseSeleccionada[idx]) return;
      if (this.selectedVideoIndex !== idx) {
         this.enviarEstadoVideo("paused");
      }
      this.selectedVideoIndex = idx;
      this.autoPlayCurrentVideo = autoPlay;
      this.cargarVideoSeleccionado();
    },
    cargarVideoSeleccionado() {
      this.detenerMonitoreoProgreso();
      let video = this.videosClaseSeleccionada[this.selectedVideoIndex];
      let nextVideoId = this.obtenerVideoId(video?.link);
      let nextVideoUrl = this.obtenerVideoUrlDirecta(video?.link);

      // Si el video seleccionado no es válido, buscar el primero que sí lo sea
      if ((!video || (!nextVideoId && !nextVideoUrl)) && this.videosClaseSeleccionada.length) {
        const idxValido = this.videosClaseSeleccionada.findIndex(v =>
          Boolean(this.obtenerVideoId(v?.link)) ||
          Boolean(this.obtenerVideoUrlDirecta(v?.link))
        );
        if (idxValido >= 0) {
          this.selectedVideoIndex = idxValido;
          video = this.videosClaseSeleccionada[idxValido];
          nextVideoId = this.obtenerVideoId(video?.link);
          nextVideoUrl = this.obtenerVideoUrlDirecta(video?.link);
        }
      }

      if (!video || (!nextVideoId && !nextVideoUrl)) {
        this.currentVideoId = null;
        this.currentVideoUrl = null;
        this.pendingStartSeconds = 0;
        return;
      }

      this.pendingStartSeconds = this.obtenerStartSecondsVideo(video);
      this.currentVideoId = nextVideoId;
      this.currentVideoUrl = nextVideoId ? null : nextVideoUrl;

      this.$nextTick(() => {
        this.aplicarInicioVideoActual();
      });
    },

    /**
     * Devuelve la URL cuando el link NO es de YouTube (video subido al sistema
     * de contenido, hoy DigitalOcean Spaces). Se apoya en obtenerVideoId para no
     * duplicar la regla: si eso resuelve un id, es de YouTube y no es directa.
     */
    obtenerVideoUrlDirecta(link) {
      const raw = String(link || "").trim();
      if (!raw || !/^https?:\/\//i.test(raw)) return null;
      return this.obtenerVideoId(raw) ? null : raw;
    },

    /**
     * Adaptador del <video> nativo a la interfaz del player de YouTube, para
     * que el tracking de progreso (getCurrentTime/getDuration/seekTo) siga
     * funcionando sin ramificar toda la logica de logs.
     */
    getPlayerHtml() {
      const el = this.$refs.videoHtml;
      if (!el) return null;
      return {
        getCurrentTime: () => Number(el.currentTime || 0),
        getDuration: () => Number(el.duration || 0),
        seekTo: seconds => {
          el.currentTime = Number(seconds) || 0;
        },
        // El navegador bloquea play() sin gesto previo del usuario y devuelve una
        // promesa rechazada. Sin el catch queda un unhandled rejection en consola;
        // el video igual se ve con sus controles para que el alumno le de play.
        playVideo: () => {
          const p = el.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        },
        pauseVideo: () => el.pause()
      };
    },
    obtenerStartSecondsVideo(videoActual) {
      if (!videoActual) return 0;
      if (videoActual.porcentaje >= 100) return 0;
      const saved = Number(
        videoActual.videoActualSegundo ||
        videoActual.video_transcurrido ||
        videoActual.videoTranscurrido ||
        0
      );
      return Number.isFinite(saved) ? Math.max(0, saved) : 0;
    },
    obtenerVideoId(link) {
      if (!link) return null;
      const raw = String(link).trim();
      if (!raw) return null;

      // Si es un ID puro de 11 caracteres
      if (!raw.includes("http")) {
        return /^[A-Za-z0-9_-]{11}$/.test(raw) ? raw : null;
      }

      try {
        const url = new URL(raw);
        const host = (url.hostname || "").toLowerCase();
        const path = url.pathname || "";

        if (host.includes("youtu.be")) {
          const id = path.replace(/^\/+/, "").split("/")[0];
          if (/^[A-Za-z0-9_-]{11}$/.test(id || "")) return id;
        }

        const v = url.searchParams.get("v");
        if (/^[A-Za-z0-9_-]{11}$/.test(v || "")) return v;

        const parts = path.split("/").filter(Boolean);
        const idxEmbed = parts.indexOf("embed");
        if (idxEmbed >= 0 && /^[A-Za-z0-9_-]{11}$/.test(parts[idxEmbed + 1] || "")) {
          return parts[idxEmbed + 1];
        }

        const idxShorts = parts.indexOf("shorts");
        if (idxShorts >= 0 && /^[A-Za-z0-9_-]{11}$/.test(parts[idxShorts + 1] || "")) {
          return parts[idxShorts + 1];
        }

        const idxLive = parts.indexOf("live");
        if (idxLive >= 0 && /^[A-Za-z0-9_-]{11}$/.test(parts[idxLive + 1] || "")) {
          return parts[idxLive + 1];
        }
        
        // Fallback para cualquier ID al final de la ruta
        const lastPart = parts[parts.length - 1];
        if (/^[A-Za-z0-9_-]{11}$/.test(lastPart || "")) return lastPart;

        return null;
      } catch (e) {
        // Fallback regex si URL falla
        const match = raw.match(/(?:v=|youtu\.be\/|embed\/|shorts\/|live\/)([A-Za-z0-9_-]{11})/i);
        return match ? match[1] : null;
      }
    },
    getPlayer() {
      if (this.currentVideoUrl) {
        return this.getPlayerHtml();
      }
      return this.$refs.youtube && this.$refs.youtube.player
        ? this.$refs.youtube.player
        : null;
    },
    async aplicarInicioVideoActual() {
      try {
        const player = this.getPlayer();
        if (!player) return;

        const startSeconds = this.pendingStartSeconds || 0;
        const videoId = this.currentVideoId;

        // Video directo: no hay loadVideoById, se posiciona con seekTo.
        if (!videoId) {
          if (!this.currentVideoUrl) return;
          if (startSeconds > 0) player.seekTo(startSeconds);
          if (this.autoPlayCurrentVideo) player.playVideo();
          return;
        }

        if (this.autoPlayCurrentVideo) {
          player.loadVideoById({ videoId, startSeconds });
        } else {
          player.cueVideoById({ videoId, startSeconds });
        }
      } catch (e) {
         // Silencio en producción
      }
    },
    // Event Handlers from Youtube Component
    onVideoReady() {
      this.aplicarInicioVideoActual();
    },
    onVideoPlaying() {
      this.iniciarMonitoreoProgreso();
      this.enviarEstadoVideo("playing");
    },
    onVideoPaused() {
      this.detenerMonitoreoProgreso();
      this.enviarEstadoVideo("paused");
    },
    async onVideoEnded() {
      this.detenerMonitoreoProgreso();
      const videoActual = this.videosClaseSeleccionada[this.selectedVideoIndex];
      if (videoActual) {
        videoActual.porcentaje = 100;
        videoActual.finalizado = true;
        await this.enviarEstadoVideo("finished");
      }
      this.irSiguienteContenido();
    },
    iniciarMonitoreoProgreso() {
      this.detenerMonitoreoProgreso();
      this.progressInterval = setInterval(() => {
        this.enviarEstadoVideo("playing");
      }, 7000); // Guardado cada 7 segundos para paridad perfecta con iea-public
    },
    detenerMonitoreoProgreso() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
    },
    async enviarEstadoVideo(tipo) {
      try {
        const videoActual = this.videosClaseSeleccionada[this.selectedVideoIndex];
        if (!videoActual || !videoActual.id || this.esDocente) return;

        const player = this.getPlayer();
        if (!player) return;

        const videoTranscurrido = Number(await player.getCurrentTime());
        const videoDuracion = Number(await player.getDuration());
        
        let videoPorcentaje = videoDuracion
          ? Math.min(100, Math.max(0, Math.round((videoTranscurrido / videoDuracion) * 100)))
          : (videoActual.porcentaje || 0);

        // Actualizamos localmente para feedback inmediato
        videoActual.videoActualSegundo = videoTranscurrido;
        videoActual.videoDuracion = videoDuracion;
        videoActual.porcentaje = videoPorcentaje;

        await this.$API.post({
          url: "materiaCodigoEstadistica/store/statusVideo",
          data: {
            alumno_id: parseInt(this.user.IDStB || 0),
            materiaCodigoClase_id: videoActual.materiaCodigoClase_id || this.claseSeleccionada?.id,
            materiaCodigoLink_id: videoActual.id,
            posicionClase: this.selectedClaseIndex,
            posicionVideo: this.selectedVideoIndex,
            videoTranscurrido,
            videoDuracion,
            videoPorcentaje,
            tipo,
            docente: this.esDocente
          }
        }, false); // Segundo parámetro false para evitar snackbar/loader cada 7 segundos
      } catch (error) {
         // Silencio para no interrumpir la experiencia
      }
    },
    async cargarDetalleClase() {
      if (!this.claseSeleccionada) return;
      this.claseDetalleLoading = true;
      try {
        const res = await this.$API.get({
          url: `materia-codigo-clase/content/${this.claseSeleccionada.id}`
        });
        this.claseDetalleHtml = res.clase;
      } catch (e) {
        this.claseDetalleHtml = "";
      } finally {
        this.claseDetalleLoading = false;
      }
    },
    async preguntarIA() {
      if (!this.iaQuestion.trim()) return;
      this.iaLoading = true;
      try {
        const res = await this.$API.post({
          url: "ia/gemini/ask",
          data: { message: this.iaQuestion }
        });
        this.iaReply = res.reply || "Análisis completado satisfactoriamente.";
      } catch (e) {
        this.iaReply = "Error en el motor de inferencia académica.";
      } finally {
        this.iaLoading = false;
      }
    },
    abrirMeet() {
      if (this.urlMeet) window.open(this.urlMeet, "_blank");
    },
    abrirRecursoCurso() {
      if (this.cursoGoogleDriveUrl)
        window.open(this.cursoGoogleDriveUrl, "_blank");
    },
    abrirRecursoPDF() {
      if (this.claseSeleccionada && this.claseSeleccionada.link_pdf) {
        window.open(this.claseSeleccionada.link_pdf, "_blank");
      }
    },
    irAnteriorContenido() {
      if (this.selectedVideoIndex > 0) {
         this.seleccionarVideo(this.selectedVideoIndex - 1);
      } else if (this.selectedClaseIndex > 0) {
         const prevClase = this.clasesMateria[this.selectedClaseIndex - 1];
         const lastVideoIdx = (prevClase.materia_codigo_link?.length || 1) - 1;
         this.seleccionarClase(this.selectedClaseIndex - 1, lastVideoIdx);
      }
    },
    irSiguienteContenido() {
      const videos = this.videosClaseSeleccionada;
      if (this.selectedVideoIndex < videos.length - 1) {
         this.seleccionarVideo(this.selectedVideoIndex + 1);
      } else if (this.selectedClaseIndex < this.clasesMateria.length - 1) {
         this.seleccionarClase(this.selectedClaseIndex + 1, 0);
      }
    },
    claseFinalizada(clase) {
      if (!clase) return false;
      
      // 1. Validar Videos
      const videos = Array.isArray(clase.materia_codigo_link) ? clase.materia_codigo_link : [];
      const videosTerminados = videos.length > 0 && videos.every(v => v.porcentaje >= 100 || v.finalizado);
      
      // 2. Validar Actividades
      const totalActividades = Number(clase.total_actividades || 0);
      const actividadesCompletadas = Number(clase.actividades_completadas || 0);
      const actividadesTerminadas = totalActividades === 0 || actividadesCompletadas >= totalActividades;

      return videosTerminados && actividadesTerminadas;
    },
    async cargarActividadesClase(claseId) {
      if (this.actividadesClaseBackendPorId[claseId]) {
        this.actividades = this.actividadesClaseBackendPorId[claseId];
        return;
      }

      this.actividadesCargando = true;
      try {
        const res = await this.$API.get({
          url: "materia-codigo-clase/actividades",
          params: { 
            cod_catedra: this.$route.params.materiaId,
            materia_codigo_clase_id: claseId 
          }
        });

        const raw = Array.isArray(res?.actividades) ? res.actividades : [];

        const mapped = raw.map(act => ({
          ...act,
          respuestaAlumno: act.entrega ? act.entrega.respuesta_texto : ""
        }));

        this.$set(this.actividadesClaseBackendPorId, claseId, mapped);
        this.actividades = mapped;
      } catch (e) {
        console.error("Error cargando actividades:", e);
        this.actividades = [];
      } finally {
        this.actividadesCargando = false;
      }
    },
    async enviarRespuestaActividad(act) {
      if (act.tipo_entrega !== "subida_pdf" && (!act.respuestaAlumno || act.respuestaAlumno.trim() === "")) return;

      this.$set(act, "enviando", true);
      try {
        let payload;
        if (act.tipo_entrega === "subida_pdf") {
          payload = new FormData();
          payload.append("pregunta_catedra_id", act.id);
          if (act.archivoAlumno) {
            payload.append("archivo", act.archivoAlumno);
          }
        } else {
          payload = {
            pregunta_catedra_id: act.id,
            respuesta_texto: act.respuestaAlumno
          };
        }

        const res = await this.$API.post({
          url: "materia-codigo-clase/entregas",
          data: payload
        });

        if (res) {
          // Actualizar estado local
          this.$set(act, "entrega", res);
          
          // Actualizar conteo en la clase para el progreso visual inmediato
          const clase = this.clasesMateria[this.selectedClaseIndex];
          if (clase) {
            // Recalculamos basándonos en si todas tienen entrega
            const completadas = this.actividades.filter(a => a.entrega).length;
            clase.actividades_completadas = completadas;
          }
          
          this.$set(this.actividadesClaseBackendPorId, act.materia_codigo_clase_id, [...this.actividades]);
          this.actualizarProgreso();
        }
      } catch (e) {
        console.error("Error enviando respuesta:", e);
      } finally {
        this.$set(act, "enviando", false);
      }
    },
    cargarForosMateria() {
      this.forosMateria = [
        {
          id: 1,
          title: "Optimización de consultas en el core",
          comments: [{}, {}, {}]
        }
      ];
    },
    abrirForoDetalle() {},
    abrirDialogForoNuevo() {},
    abrirDetalleClase() {}
  },
  mounted() {
    this.cargarClasesMateria();
  },
  beforeDestroy() {
     this.detenerMonitoreoProgreso();
     this.enviarEstadoVideo("paused");
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Outfit:wght@700;900&display=swap");

.prism-elite-app {
  font-family: "Inter", sans-serif !important;
  background: radial-gradient(circle at 0% 0%, #f1f5f9 0%, #ffffff 100%);
  min-height: 100vh;
}

h1,
h2,
h3,
.text-overline,
.prism-heading {
  font-family: "Outfit", sans-serif !important;
  letter-spacing: -0.5px;
}

.uppercase { text-transform: uppercase; }
.letter-spaced { letter-spacing: 2px !important; }
.text-tiny { font-size: 0.8rem !important; }
.lh-1 { line-height: 1 !important; }

/* Cinema Mode Logic */
.cinema-mode-active {
  background: #000 !important;
}
.cinema-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 50;
  backdrop-filter: blur(10px);
}
.cinema-focus {
  position: relative;
  z-index: 100;
  max-width: 1000px !important;
  margin: 0 auto;
}

/* Glassmorphism Styles */
.main-container-glass {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(20px);
}

.prism-top-bar {
  background: transparent;
}

.glass-sidebar {
  background: rgba(248, 250, 252, 0.6) !important;
  border-right: 1px solid rgba(226, 232, 240, 0.5);
}

.glass-card-light {
  background: rgba(255, 255, 255, 0.5) !important;
  border: 1px solid rgba(226, 232, 240, 0.8) !important;
}

.glass-card-heavy {
  background: white;
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.05) !important;
}

.glass-card-interactive {
  background: white;
  border: 1px solid #f1f5f9 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.glass-card-interactive:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.1) !important;
}

/* Navigator Prism */
.prism-nav-item {
  border-radius: 16px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s;
  background: transparent;
  overflow: hidden;
}
.prism-nav-item:hover {
  background: rgba(255, 255, 255, 0.9);
}
.prism-nav-item.is-active {
  background: white;
  box-shadow: 0 8px 16px -4px rgba(0,0,0,0.05);
}
.active-glow {
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 4px;
  background: #1a237e;
  opacity: 0;
  transition: opacity 0.3s;
}
.is-active .active-glow { opacity: 1; }

.status-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
}
.bg-success { background: #10b981; }
.bg-grey { background: #cbd5e1; }

.prism-nav-title {
  font-size: 1rem !important;
  line-height: 1.4;
}

/* Video Stage Prism */
.prism-video-card {
  border-radius: 24px;
  background: #000;
  overflow: hidden;
  box-shadow: 0 30px 60px -12px rgba(0,0,0,0.25);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.prism-controls { background: #ffffff; }
.glass-btn-sm {
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(4px);
}
.video-pill {
  width: 12px !important;
  height: 4px !important;
  background: #e2e8f0 !important;
  cursor: pointer;
  transition: all 0.3s;
}
.active-pill {
  width: 24px !important;
  background: #1a237e !important;
}

.glass-action-btn {
  background: #f1f5f9 !important;
  text-transform: none;
  font-weight: 700;
  border-radius: 12px;
}
.primary-glass { background: #1a237e !important; color: white !important; }

/* Tabs Prism */
.prism-tabs {
  background: #f8fafc;
  padding: 6px;
  border-radius: 20px;
  display: inline-flex;
}
.prism-tab-item {
  border-radius: 16px !important;
  min-width: 140px;
  font-weight: 700;
  color: #64748b !important;
}
.v-tab--active {
  background: white;
  color: #1a237e !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

/* IA Console Prism */
.prism-ai-console {
  background: #0f172a;
  color: white;
  box-shadow: 0 20px 40px -10px rgba(15, 23, 42, 0.3) !important;
}
.elite-ia-avatar {
  width: 44px; height: 44px;
  background: #3b82f6;
  border-radius: 16px;
  display: flex; align-items: center; justify-content: center;
  position: relative;
}
.ia-ring {
  position: absolute;
  inset: -3px;
  border: 2px solid #3b82f6;
  border-radius: 19px;
  animation: pulse-ring 2s infinite;
}
@keyframes pulse-ring {
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(1.2); opacity: 0; }
}

.prism-ai-viewport {
  height: 280px;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(0, 0, 0, 0.2);
  border-radius: 20px;
  padding: 15px;
}
.ai-elite-bubble {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
}
.text-assistant { color: #e2e8f0; line-height: 1.7; }

.elite-textarea ::v-deep fieldset { border-color: rgba(255, 255, 255, 0.1) !important; }
.elite-textarea ::v-deep textarea { color: white !important; font-size: 0.9rem; }
.elite-textarea ::v-deep .v-label { color: rgba(255, 255, 255, 0.7) !important; }
.elite-textarea ::v-deep input::placeholder,
.elite-textarea ::v-deep textarea::placeholder { color: rgba(255, 255, 255, 0.7) !important; }

/* Meet Elite */
.elite-meet-card {
  background: #fef2f2;
  border: 1px solid #fee2e2 !important;
  cursor: pointer;
  transition: all 0.3s;
}
.elite-meet-card:hover { border-color: #f87171 !important; transform: scale(1.02); }
.live-pulse {
  width: 8px; height: 8px;
  background: #ef4444;
  border-radius: 50%;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.2);
  animation: pulse-red 1.5s infinite;
}
@keyframes pulse-red {
  0% { box-shadow: 0 0 0 0px rgba(239, 68, 68, 0.4); }
  100% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
}

/* Activity Prism */
.prism-drop-row { background: #f8fafc; border-radius: 16px; border: 1px solid #e2e8f0; }
.prism-drop-target {
  min-width: 140px; background: white; border: 2px dashed #cbd5e1;
  padding: 10px 20px; border-radius: 12px; font-size: 0.75rem;
  color: #94a3b8; text-align: center; transition: all 0.3s;
}
.has-content { border-style: solid; border-color: #1a237e; color: #1a237e; background: #f0f4ff; font-weight: 800; }
.prism-drag-chip {
  background: white !important; border: 1px solid #e2e8f0 !important;
  font-weight: 700; color: #1a237e !important; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1) !important;
}

.quiz-option {
  border-radius: 16px; border: 1px solid #f1f5f9; background: #f8fafc;
  cursor: pointer; transition: all 0.2s;
}
.quiz-option.is-active { border-color: #1a237e; background: #f0f4ff; box-shadow: 0 8px 20px -8px rgba(26, 35, 126, 0.2); }
.quiz-radio { width: 18px; height: 18px; border: 2px solid #cbd5e1; border-radius: 50%; position: relative; }
.is-active .quiz-radio { border-color: #1a237e; }
.is-active .quiz-radio::after { content: ''; position: absolute; inset: 3px; background: #1a237e; border-radius: 50%; }

/* Misc */
.animate-scale-up { animation: scale-up 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
@keyframes scale-up { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
.fade-in { animation: fade-in 0.5s ease-out; }
@keyframes fade-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.content-render ::v-deep img { max-width: 100%; border-radius: 20px; box-shadow: 0 10px 30px rgba(0,0,0,0.1); }
</style>
